import * as React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  makeStyles,
  Theme,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  LinearProgress,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  useEnqueueBbSnackbar,
} from '@bb-ui/react-library';
import { Switch } from '@bb-ui/react-library/dist/components/Switch';
import { FormControlLabel } from '@bb-ui/react-library/dist/components/FormControlLabel';
import { Start } from '@bb-ui/icons/dist/medium';
import { useTranslation } from 'react-i18next';
import Form from '@rjsf/mui';
import {
  IJobProvider,
  IJobSummary,
  ListJobsResponseBody,
  JobDetailResponseBody,
} from '@lct-jobs/core';
import { IChangeEvent } from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { useHistory } from 'react-router';
import useRestApi from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { useSnackbar } from 'hooks/useSnackbar';
import { sanitizeDefaults } from 'components/JobList/SanitizeDefaults';
import { LearnInstanceItem } from './MassQueryTooling.types';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    marginTop: theme.spacing(3),
    flexShrink: 0,
  },
  dialogForm: {
    '& div[role="radiogroup"] label span:first-child': {
      width: '32px',
      height: '32px',
      borderRadius: '16px',
      '&:hover': {
        backgroundColor: theme.palette.background.b4,
      },
    },
  },
}));

interface MassJobFormProps {
  selectedRegion: string;
  selectedSites: LearnInstanceItem[];
}

export const MassJobForm: React.FC<MassJobFormProps> = ({ selectedRegion, selectedSites }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const classes = useStyles();
  const history = useHistory();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedJob, setSelectedJob] = React.useState<IJobSummary | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [formData, setFormData] = React.useState<any>({});
  const [updatedSchema, setUpdatedSchema] = React.useState<any>(null);
  const [selectedProvider, setSelectedProvider] = React.useState<IJobProvider | null>(null);
  const [scheduleEnabled, setScheduleEnabled] = React.useState(false);
  const [scheduledDateTime, setScheduledDateTime] = React.useState('');
  const [maxConcurrency, setMaxConcurrency] = React.useState('');

  const jobListApi = useRestApi<ListJobsResponseBody>(
    apiUrl('lct', `regions/${selectedRegion}/tenantTypes/LearnInstance/jobManager/jobs`),
  );

  const jobDetailApi = useRestApi<JobDetailResponseBody>(
    selectedJob
      ? apiUrl(
          'lct',
          `regions/${selectedRegion}/tenantTypes/LearnInstance/jobManager/jobs/${selectedJob.jobId}`,
        )
      : '',
    { manual: true },
  );

  const executionApi = useRestApi(apiUrl('lct', `mass-job/regions/${selectedRegion}/execute`), {
    manual: true,
  });

  const visibleJobs = React.useMemo(() => {
    const jobs = jobListApi.data?.jobs ?? [];
    return jobs.filter((job) => job.visible);
  }, [jobListApi.data]);

  const filteredJobs = React.useMemo(() => {
    if (!searchQuery) return visibleJobs;
    const lower = searchQuery.toLowerCase();
    return visibleJobs.filter(
      (job) =>
        (job.jobName?.toLowerCase() || '').includes(lower) ||
        (job.jobId?.toLowerCase() || '').includes(lower) ||
        (job.jobDescription?.toLowerCase() || '').includes(lower),
    );
  }, [visibleJobs, searchQuery]);

  React.useEffect(() => {
    if (jobDetailApi.data?.providers) {
      const providers: IJobProvider[] = Object.values(jobDetailApi.data.providers);
      if (providers.length > 0) {
        const provider = providers[0];
        setSelectedProvider(provider);

        const inputSchemaWithProperties = provider.inputSchema as unknown as {
          properties: { [key: string]: any };
        };

        const newSchema = {
          ...inputSchemaWithProperties,
          properties: { ...inputSchemaWithProperties.properties },
        };

        if (jobDetailApi.data.resolvedAutoFillKeys && newSchema.properties) {
          for (const [key, resolvedKey] of Object.entries(jobDetailApi.data.resolvedAutoFillKeys)) {
            for (const [propKey, property] of Object.entries(newSchema.properties)) {
              if (property.autofillKey === key) {
                if (resolvedKey.defaultValue) {
                  property.default = resolvedKey.defaultValue;
                }
                if (resolvedKey.examples) {
                  property.examples = resolvedKey.examples;
                }
                if (resolvedKey.enum) {
                  const enumKeys = Object.keys(resolvedKey.enum);
                  [property.default] = enumKeys;
                  property.enum = enumKeys;

                  const resolvedEnum = resolvedKey.enum;
                  const autofillOptions = enumKeys.map((enumKey) => ({
                    title: enumKey,
                    const: resolvedEnum[enumKey]?.[propKey] ?? enumKey,
                  }));
                  const hasMappedValues = autofillOptions.some(
                    (option) => option.title !== option.const,
                  );

                  if (hasMappedValues) {
                    property.default = autofillOptions[0]?.const;
                    property.oneOf = autofillOptions;
                    delete property.enum;
                  }
                }
              }
            }
          }
        }

        sanitizeDefaults(newSchema.properties);
        setUpdatedSchema(newSchema);
      } else {
        setSelectedProvider(null);
        setUpdatedSchema(null);
      }
    }
  }, [jobDetailApi.data]);

  const executionIdRef = React.useRef<string | undefined>();

  const showSuccessSnackbar = useEnqueueBbSnackbar(
    { snackbarMessage: t('massQueryTooling.massJob.submitSuccess') },
    {
      actionMessage: t('massQueryTooling.massJob.viewExecutions'),
      onActionClick: () => {
        const executionId = executionIdRef.current;
        if (executionId && selectedRegion) {
          history.push(`/query-tooling/regions/${selectedRegion}/executions/${executionId}`);
          closeSnackbar();
        }
      },
      closeButtonAriaLabel: 'close',
    },
  );

  React.useEffect(() => {
    if (executionApi.succeededRequests.length > 0) {
      const result = executionApi.succeededRequests.at(0);
      const executionId: string | undefined =
        result?.response?.data?.jobExecutionId || result?.response?.data?.executionId;

      executionIdRef.current = executionId;
      showSuccessSnackbar();
      executionApi.clearSucceededRequests();
      setDialogOpen(false);
      setSelectedJob(null);
      setFormData({});
      setUpdatedSchema(null);
      setSelectedProvider(null);
    }
  }, [executionApi.succeededRequests, executionApi, showSuccessSnackbar]);

  React.useEffect(() => {
    if (executionApi.failedRequests.length > 0) {
      enqueueSnackbar(t('massQueryTooling.massJob.submitError'), { variant: 'error' });
      executionApi.clearFailedRequests();
    }
  }, [executionApi.failedRequests, enqueueSnackbar, t, executionApi]);

  React.useEffect(() => {
    if (jobListApi.error) {
      enqueueSnackbar(t('massQueryTooling.massJob.loadError'), { variant: 'error' });
    }
  }, [jobListApi.error, enqueueSnackbar, t]);

  const handleViewJob = (job: IJobSummary) => {
    setSelectedJob(job);
    setFormData({});
    setUpdatedSchema(null);
    setSelectedProvider(null);
    setDialogOpen(true);
  };

  React.useEffect(() => {
    if (dialogOpen && selectedJob) {
      jobDetailApi.fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, selectedJob]);

  const handleFormChange = ({ formData: newFormData }: { formData: any }) => {
    setFormData(newFormData);
  };

  const handleSubmit = (s: IChangeEvent<any>) => {
    if (!selectedJob || !selectedProvider) return;

    const scheduledStartTime =
      scheduleEnabled && scheduledDateTime ? new Date(scheduledDateTime).toISOString() : undefined;

    const parsedMaxConcurrency = maxConcurrency.trim() !== '' ? Number(maxConcurrency) : undefined;

    const body = {
      tenantList: selectedSites.map((site) => site.TenantId).filter((id): id is string => !!id),
      massJob: {
        jobId: selectedJob.jobId,
        jobInput: s.formData,
        providerId: selectedProvider.providerId,
      },
      ...(scheduledStartTime && { scheduledStartTime }),
      ...(parsedMaxConcurrency !== undefined && { maxConcurrency: parsedMaxConcurrency }),
    };

    enqueueSnackbar(t('massQueryTooling.massJob.submitting'), { variant: 'info' });
    executionApi.doPost(body);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedJob(null);
    setFormData({});
    setUpdatedSchema(null);
    setSelectedProvider(null);
    setScheduleEnabled(false);
    setScheduledDateTime('');
  };

  const canRenderForm = !jobDetailApi.loading && selectedProvider && updatedSchema;

  return (
    <>
      <Card className={classes.card} raised>
        <CardHeader title={t('massQueryTooling.massJob.title')} />
        <CardContent>
          {jobListApi.loading && <LinearProgress ariaLabel="Loading Jobs" />}

          {!jobListApi.loading && jobListApi.error && (
            <Typography color="error">{t('massQueryTooling.massJob.loadError')}</Typography>
          )}

          {!jobListApi.loading && !jobListApi.error && (
            <>
              <TextField
                floatingLabel
                label={t('massQueryTooling.massJob.searchJobs')}
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchQuery(e.target.value)
                }
                margin="normal"
              />
              <Table>
                <TableHead>
                  <TableRow key="$HEADER">
                    <TableCell>{t('jobService.name')}</TableCell>
                    <TableCell>{t('jobService.domain')}</TableCell>
                    <TableCell>{t('jobService.description')}</TableCell>
                    <TableCell>{t('jobService.submitJob')}</TableCell>
                  </TableRow>
                </TableHead>
                <tbody>
                  {filteredJobs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} style={{ textAlign: 'center' }}>
                        <Typography>{t('massQueryTooling.massJob.noJobs')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredJobs.map((job) => (
                      <TableRow key={job.jobId}>
                        <TableCell>{job.jobName}</TableCell>
                        <TableCell>{job.jobDomain}</TableCell>
                        <TableCell>{job.jobDescription}</TableCell>
                        <TableCell>
                          <IconButton onClick={() => handleViewJob(job)}>
                            <Start />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </tbody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      {dialogOpen && selectedJob && (
        <Dialog id="mass-job-run-dialog" open={dialogOpen} onClose={handleCloseDialog}>
          <DialogTitle onClose={handleCloseDialog} closeLabel={t('global.close')}>
            {selectedJob.jobName}
          </DialogTitle>
          <DialogContent>
            {jobDetailApi.loading && (
              <Typography variant="body2" color="textSecondary">
                {t('massQueryTooling.massJob.loadingJobDetail')}
              </Typography>
            )}

            {jobDetailApi.error && (
              <Typography color="error">{t('massQueryTooling.massJob.loadDetailError')}</Typography>
            )}

            {canRenderForm && (
              <>
                <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        id="mass-job-schedule-toggle"
                        checked={scheduleEnabled}
                        onChange={() => setScheduleEnabled(!scheduleEnabled)}
                      />
                    }
                    label={t('jobService.scheduleForLater')}
                  />
                  {scheduleEnabled && (
                    <TextField
                      id="mass-job-scheduled-start-time"
                      label={t('jobService.scheduledStartTime')}
                      type="datetime-local"
                      value={scheduledDateTime}
                      onChange={(e: any) => setScheduledDateTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      inputProps={{ min: new Date().toISOString().slice(0, 16) }}
                      style={{ marginTop: '0.5rem' }}
                    />
                  )}
                  <TextField
                    id="mass-job-max-concurrency"
                    label={t('massQueryTooling.massJob.maxConcurrency')}
                    type="number"
                    value={maxConcurrency}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setMaxConcurrency(e.target.value)
                    }
                    helperText={t('massQueryTooling.massJob.maxConcurrencyHelper')}
                    inputProps={{ min: 1, max: 150 }}
                    fullWidth
                    style={{ marginTop: '0.5rem' }}
                  />
                </div>
                <Form
                  schema={updatedSchema}
                  formData={formData}
                  validator={validator}
                  onChange={(data: IChangeEvent) => handleFormChange({ formData: data.formData })}
                  onSubmit={handleSubmit}
                  uiSchema={selectedProvider.inputUiSchema}
                  className={classes.dialogForm}
                  disabled={executionApi.loadingRequests}
                />
              </>
            )}

            {!canRenderForm && !jobDetailApi.loading && !jobDetailApi.error && (
              <Typography variant="body2" color="textSecondary">
                {t('massQueryTooling.massJob.noProviders')}
              </Typography>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
